import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, FormControl, InputLabel, Select, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, Typography, Avatar, Chip, Divider, TextField, InputAdornment, Paper, CircularProgress } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import { formatCurrency, formatDateTime, formatDuration } from '../../utils/formatters';
import { getChatRooms, getChatMessages } from '../../api/endpoints/chat';

const ChatLogs = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [viewRoom, setViewRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchRooms = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await getChatRooms(params);
      setRooms(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      if (err.response?.status !== 404) {
        toast.error(err.response?.data?.message || 'Failed to load chat rooms');
      }
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const handleViewRoom = async (room) => {
    setViewRoom(room);
    setMessagesLoading(true);
    try {
      const res = await getChatMessages(room._id, { limit: 100 });
      setMessages(res.data.data || []);
    } catch (err) {
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const columns = [
    { id: '_id', label: 'Room ID', render: (row) => <Typography variant="body2" fontFamily="monospace" fontWeight={500}>{(row._id || '').slice(-8)}</Typography> },
    { id: 'user', label: 'User', render: (row) => (
      <Box>
        <Typography variant="body2" fontWeight={500}>{row.userId?.name || row.user?.name || '-'}</Typography>
        <Typography variant="caption" color="text.secondary">{row.userId?.phone || row.user?.phone || ''}</Typography>
      </Box>
    )},
    { id: 'astrologer', label: 'Astrologer', render: (row) => row.astrologerId?.name || row.astrologer?.name || '-' },
    { id: 'duration', label: 'Duration', render: (row) => formatDuration(row.duration) },
    { id: 'cost', label: 'Cost', render: (row) => formatCurrency(row.cost || row.amount || 0) },
    { id: 'messageCount', label: 'Msgs', render: (row) => row.messageCount || row.messages || 0 },
    { id: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
    { id: 'createdAt', label: 'Date', render: (row) => formatDateTime(row.createdAt) },
    { id: 'actions', label: '', render: (row) => (
      <IconButton size="small" onClick={() => handleViewRoom(row)}><VisibilityIcon fontSize="small" /></IconButton>
    )},
  ];

  return (
    <Box>
      <PageHeader title="Chat Logs" subtitle="View all chat sessions between users and astrologers" />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search..."
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ minWidth: 300 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <DataTable
        columns={columns}
        rows={rooms}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        loading={loading}
      />

      <Dialog open={!!viewRoom} onClose={() => setViewRoom(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Chat Messages
          <Typography variant="caption" display="block" color="text.secondary">
            {viewRoom?.userId?.name || viewRoom?.user?.name} with {viewRoom?.astrologerId?.name || viewRoom?.astrologer?.name}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ maxHeight: 500, overflowY: 'auto' }}>
          {messagesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : messages.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No messages found</Typography>
          ) : (
            <Stack spacing={1.5}>
              {messages.map((msg) => (
                <Box
                  key={msg._id || msg.id}
                  sx={{
                    display: 'flex',
                    justifyContent: msg.sender === 'astrologer' || msg.senderType === 'astrologer' ? 'flex-start' : 'flex-end',
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      maxWidth: '75%',
                      backgroundColor: msg.sender === 'astrologer' || msg.senderType === 'astrologer' ? '#F5F0FF' : '#FFF8E1',
                      borderRadius: 2,
                      borderTopLeftRadius: msg.sender === 'astrologer' || msg.senderType === 'astrologer' ? 0 : 2,
                      borderTopRightRadius: msg.sender === 'user' || msg.senderType === 'user' ? 0 : 2,
                    }}
                  >
                    <Typography variant="body2">{msg.text || msg.message || ''}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {formatDateTime(msg.createdAt || msg.timestamp)}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ChatLogs;